import React, { useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import CustomSelect from 'components/form/CustomSelect';
import TextArea from 'components/form/TextArea';
import UserSelect from 'components/form/UserSelect';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import { MasterTypeResult } from 'stores/MasterTypeStoreAdapter';
import {
  TicketDetailResult,
  updateApprovalTicket,
  updateTaskTicket,
} from 'stores/TicketStoreAdapter';
import { getTicketType } from 'stores/TicketTypeStoreAdapter';
import { successDefault, toErrorDetail } from 'utils/Toastify';

import TicketFormFields, { buildFormValues, buildValuesFromDetail } from './TicketFormFields';

type Props = {
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
  detail: TicketDetailResult;
  priorities: MasterTypeResult[];
};

/**
 * チケット編集ダイアログ（タスク/承認）。
 * - タスク: タイトル/説明/優先度/担当者/フォーム値
 * - 承認: タイトル/説明/承認者/フォーム値（ADJUSTING/REJECTED のみ・作成者のみ）
 */
const TicketEditDialog: React.FC<Props> = ({ open, onClose, onUpdated, detail, priorities }) => {
  const { users } = useAuth();
  const isTask = detail.ticketCategory === 'TASK';
  const isPrivate = detail.scope === 'PRIVATE';

  const [title, setTitle] = useState(detail.title);
  const [description, setDescription] = useState(detail.description ?? '');
  const [priority, setPriority] = useState(detail.priority ?? '');
  const [assigneeUserId, setAssigneeUserId] = useState<string | null>(detail.assigneeUserId);
  const [approverUserId, setApproverUserId] = useState<string | null>(detail.approverUserId);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [notify, setNotify] = useState(false);
  const [formTypeByKey, setFormTypeByKey] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(detail.title);
    setDescription(detail.description ?? '');
    setPriority(detail.priority ?? '');
    setAssigneeUserId(detail.assigneeUserId);
    setApproverUserId(detail.approverUserId);
    setNotify(false);
    setErrors({});
    setFormValues(buildValuesFromDetail(detail.formGroups, detail.unassignedItems));
    const map: Record<string, string> = {};
    detail.formGroups.forEach((g) => g.items.forEach((it) => (map[it.itemKey] = it.formType)));
    detail.unassignedItems.forEach((it) => (map[it.itemKey] = it.formType));
    setFormTypeByKey(map);
  }, [open, detail]);

  const priorityOptions = useMemo(
    () => priorities.map((p) => ({ value: p.typeCode, label: p.typeLabel })),
    [priorities],
  );

  // 種別テンプレート（フォーム項目の描画に必要）
  const [tmplData, setTmplData] = useState<Awaited<ReturnType<typeof getTicketType>> | null>(null);
  useEffect(() => {
    if (!open) return;
    getTicketType({ typeId: detail.typeId, visibleOnly: true })
      .then(setTmplData)
      .catch(() => setTmplData(null));
  }, [open, detail.typeId]);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!title.trim()) e.title = MSG_REQUIRED('タイトル');
    else if (title.length > 300) e.title = MSG_MAX_LENGTH('タイトル', 300);
    if (isTask && !assigneeUserId) e.assigneeUserId = MSG_REQUIRED('担当者');
    if (!isTask && !approverUserId) e.approverUserId = MSG_REQUIRED('承認者');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate() || submitting) return;
    setSubmitting(true);
    const values = buildFormValues(formValues, formTypeByKey);
    const promise = isTask
      ? updateTaskTicket({
          ticketId: detail.ticketId,
          title: title.trim(),
          description: description.trim() || undefined,
          priority: priority || undefined,
          assigneeUserId: assigneeUserId as string,
          formValues: values,
          notify,
        })
      : updateApprovalTicket({
          ticketId: detail.ticketId,
          title: title.trim(),
          description: description.trim() || undefined,
          approverUserId: approverUserId as string,
          formValues: values,
          notify,
        });
    promise
      .then(() => {
        successDefault('更新しました');
        onUpdated();
        onClose();
      })
      .catch((e) => setErrors({ submit: toErrorDetail(e) ?? '更新に失敗しました' }))
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        elevation: 0,
        sx: { borderRadius: 3, border: 1, borderColor: 'divider', height: '88vh' },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          チケット編集（{detail.managementId}）
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ overflow: 'auto' }}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* タイトル */}
          <TextField
            size="small"
            fullWidth
            required
            label="タイトル"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={!!errors.title}
            helperText={errors.title}
            slotProps={{ htmlInput: { maxLength: 300 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          {/* 担当者（タスク・PRIVATE以外） */}
          {isTask && !isPrivate && (
            <UserSelect
              mode="single"
              label="担当者"
              required
              users={users}
              selectedUserId={assigneeUserId}
              onChange={setAssigneeUserId}
              error={!!errors.assigneeUserId}
              helperText={errors.assigneeUserId}
            />
          )}

          {/* 承認者（承認） */}
          {!isTask && (
            <UserSelect
              mode="single"
              label="承認者"
              required
              users={users}
              selectedUserId={approverUserId}
              onChange={setApproverUserId}
              error={!!errors.approverUserId}
              helperText={errors.approverUserId}
            />
          )}

          {/* 優先度（タスク） */}
          {isTask && (
            <CustomSelect
              mode="single"
              label="優先度"
              options={priorityOptions}
              value={priority}
              onChange={setPriority}
              placeholder="優先度を選択（任意）"
            />
          )}

          {/* 説明（担当者の下） */}
          <TextArea
            label="説明"
            value={description}
            onChange={setDescription}
            maxLength={10000}
            rows={4}
            fullWidth
          />

          {tmplData && (
            <TicketFormFields
              formGroups={tmplData.results}
              unassignedItems={tmplData.unassignedItems}
              values={formValues}
              onChange={(k, v) => setFormValues((prev) => ({ ...prev, [k]: v }))}
            />
          )}

          {!isPrivate && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                />
              }
              label={<Typography variant="body2">担当者・ウォッチャーへ通知する</Typography>}
            />
          )}

          {errors.submit && <Alert severity="error">{errors.submit}</Alert>}
        </Stack>
      </DialogContent>

      {/* アクション（固定フッター・スクロール不要で常に押せる） */}
      <DialogActions sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          disabled={submitting}
          onClick={handleSubmit}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          更新
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketEditDialog;
